// /src/components/PublicLexicalPage.jsx

import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import useImageModal from '../hooks/useImageModal';
import ImageModal from './ImageModal';
import PublicTocPanel from './PublicTocPanel';
import { extractPublicToc } from '../../utils/extractPublicToc';
import collapse from '../medias/collapse.png';

function PublicLexicalPage({ documentName, layout="classic", wrapperClass="", onTocReady, onTocVisibleChange, }) {
  const STYLE_ID = `style-${documentName}`;

  const { modalIsOpen, selectedImageSrc, openModal, closeModal } = useImageModal();
  const publicContainerRef = useRef(null);

  const [publicContent, setPublicContent] = useState('');
  const [hash, setHash] = useState(null);

  const [showPublicToc, setShowPublicToc] = useState(false);
  const [toc, setToc] = useState([]);
  const [tocVisible, setTocVisible] = useState(false);

    useEffect(() => {
    if (!onTocReady) return;
    if (!publicContainerRef.current) return;

    onTocReady({
        showPublicToc,
        toc,
        tocVisible,
        setTocVisible,
        containerRef: publicContainerRef
    });

    }, [
    showPublicToc,
    toc,
    tocVisible,
    publicContainerRef.current
    ]);


  // English comment: fetch HTML + CSS + toc flag
  const fetchRteContents = async () => {
    try {
      const resp = await axios.get('/api/admin/lexical/retrieve-public-html-document', {
        params: { documentName },
      });

      const { htmlContent, cssPublic, contentHash, showPublicToc: tocFlag } = resp.data.data;

      setPublicContent(htmlContent || '');
      setShowPublicToc(!!tocFlag);

      if (tocFlag) {
        const t = extractPublicToc(htmlContent);
        setToc(t);
      }

      let styleEl = document.getElementById(STYLE_ID);
      const prevHash = styleEl?.getAttribute('data-hash') || null;

      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = STYLE_ID;

        const meta = document.querySelector('meta[name="csp-nonce"]');
        const nonce = meta && meta.content;
        if (nonce) styleEl.setAttribute('nonce', nonce);

        document.head.appendChild(styleEl);
      }

      if (contentHash && contentHash !== prevHash) {
        styleEl.textContent = cssPublic || '';
        styleEl.setAttribute('data-hash', contentHash);
        setHash(contentHash);
      }

    } catch (err) {
      console.error('Error fetching public HTML+CSS:', err);
      setPublicContent('');
    }
  };

  useEffect(() => {
    fetchRteContents();
  }, []);

  // English comment: click image → modal
  useEffect(() => {
    const handleImageClick = (event) => {
      if (
        event.target.className.includes('e-rte-image') &&
        !event.target.className.includes('rte-emoji')
      ) {
        openModal(event.target.src);
      }
    };

    const c = publicContainerRef.current;
    if (c) {
      c.addEventListener('click', handleImageClick);
      return () => c.removeEventListener('click', handleImageClick);
    }
  }, [openModal]);

  // English comment: Trusted Types
  const trustedHTML = useMemo(() => {
    if (window.trustedTypes) {
      try {
        const policy = window.trustedTypes.createPolicy('pallasPages', {
          createHTML: (i) => i,
          createScript: (i) => i,
          createScriptURL: (i) => i,
        });
        return policy.createHTML(publicContent);
      } catch {
        return window.trustedTypes.createPolicy('pallasPages').createHTML(publicContent);
      }
    }
    return publicContent;
  }, [publicContent]);

  useEffect(() => {
    // English comment: notify parent when toc visibility changes
    if (typeof onTocVisibleChange === "function") {
      onTocVisibleChange(tocVisible);
    }
  }, [tocVisible, onTocVisibleChange]);

  const handleTocToggle = () => {
    if (!tocVisible) {
      setTocVisible(true);
    } else {
      window.dispatchEvent(new Event("close-public-toc"));
    }
  };

    // ---------- RENDER SWITCH ----------
        if (layout === "plain") {
        return (
            <div className={wrapperClass}>
                <div className="public-container margin-bottom-spacer">
                    {showPublicToc && (
                        <div className="public-toc-button-container" onClick={() => setTocVisible(!tocVisible)}>
                            <img
                            src={collapse}
                            alt="Toggle TOC"
                            className={`public-toc-icon ${tocVisible ? "toc-open" : "toc-closed"}`}
                            />
                        </div>
                    )}

                    <div
                    ref={publicContainerRef}
                    className="public-rte-content-container lexical-rendered-content"
                    data-doc={documentName}
                    role="document"
                    dangerouslySetInnerHTML={{ __html: trustedHTML }}
                    />
                </div>

                <ImageModal
                    isOpen={modalIsOpen}
                    onClose={closeModal}
                    src={selectedImageSrc}
                    className="e-rte-image-custom-modal-content"
                />
            </div>
        );
    }


  // ---------- DEFAULT CLASSIC LAYOUT ----------
  return (
    <div>
      <div className="title-container margin-top-spacer-60">
        <div className="trapezoid-border2">
          <a className="trapezoid-title">
            <span>{documentName}</span>
          </a>
        </div>
      </div>

      {tocVisible && showPublicToc && (
        <PublicTocPanel
          toc={toc}
          containerRef={publicContainerRef}
          onClose={() => setTocVisible(false)}
        />
      )}

      <div
        className={
          `public-container glass-card margin-bottom-spacer` +
          (tocVisible ? " public-container-shifted" : "")
        }
      >
        {showPublicToc && (
          <div className="public-toc-button-container" onClick={handleTocToggle}>
            <img
              src={collapse}
              alt="Toggle TOC"
              className={`public-toc-icon ${tocVisible ? 'toc-open' : 'toc-closed'}`}
            />
          </div>
        )}

        <div
          ref={publicContainerRef}
          className="public-rte-content-container lexical-rendered-content"
          data-doc={documentName}
          role="document"
          dangerouslySetInnerHTML={{ __html: trustedHTML }}
        />
      </div>

      <ImageModal
        isOpen={modalIsOpen}
        onClose={closeModal}
        src={selectedImageSrc}
        className="e-rte-image-custom-modal-content"
      />
    </div>
  );
}

export default PublicLexicalPage;
